// React imports
import { useEffect, useState, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';

// Generic imports
import axios from 'axios';
import _ from 'lodash';

// MUI Imports
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import LinearProgress from '@mui/material/LinearProgress';
import Chip from '@mui/material/Chip';

function Releases(props) {
    const { accesstoken } = useOutletContext();
    const [loading, setLoading] = useState(false);
    const [releases, setReleases] = useState([]);
    const [currentVersion, setCurrentVersion] = useState(null);
    const [error, setError] = useState(null);
    const [deployDialogOpen, setDeployDialogOpen] = useState(false);
    const [selectedVersion, setSelectedVersion] = useState('');
    const [deploying, setDeploying] = useState(false);
    const [deployStatus, setDeployStatus] = useState(null);
    const pollingCancelledRef = useRef(false);

    useEffect(() => {
        fetchReleases();
        fetchCurrentVersion();
    }, []);

    async function fetchReleases() {
        setLoading(true);
        setError(null);
        try {
            const { data } = await axios.get(`/api/admin/releases?accesstoken=${accesstoken}`);
            setReleases(data.releases || []);
        } catch (e) {
            setError('Failed to fetch releases: ' + (e.response?.data?.detail || e.message));
        } finally {
            setLoading(false);
        }
    }

    async function fetchCurrentVersion() {
        try {
            const response = await axios.get('https://app.monada.ai/build-time', {
                timeout: 5000,
            });
            setCurrentVersion(response.data);
        } catch (e) {
            console.error('Failed to fetch current version:', e);
            setCurrentVersion('Unable to fetch');
        }
    }

    function handleDeployClick(version) {
        setSelectedVersion(version);
        setDeployDialogOpen(true);
        setDeployStatus(null);
    }

    function handleDeployCancel() {
        // Cancel polling if in progress
        pollingCancelledRef.current = true;
        setDeployDialogOpen(false);
        setSelectedVersion('');
        setDeployStatus(null);
        setDeploying(false);
    }

    async function handleDeployConfirm() {
        if (!selectedVersion) return;

        setDeploying(true);
        setDeployStatus(null);
        setError(null);
        pollingCancelledRef.current = false;

        try {
            // Get current build-time before deployment
            let oldBuildTime;
            try {
                const buildTimeResponse = await axios.get('https://app.monada.ai/build-time', {
                    timeout: 5000,
                });
                oldBuildTime = buildTimeResponse.data;
            } catch (e) {
                console.warn('Could not fetch current build-time:', e.message);
            }

            // Start the deployment
            const { data } = await axios.post(
                `/api/admin/releases/deploy?accesstoken=${accesstoken}`,
                { version: selectedVersion }
            );

            // Poll for build-time change (wait up to 5 minutes)
            const maxWaitTime = 5 * 60 * 1000; // 5 minutes
            const pollInterval = 5000; // 5 seconds
            const startTime = Date.now();
            let newBuildTime = oldBuildTime;
            let buildTimeChanged = false;

            while (Date.now() - startTime < maxWaitTime && !buildTimeChanged && !pollingCancelledRef.current) {
                try {
                    const buildTimeResponse = await axios.get('https://app.monada.ai/build-time', {
                        timeout: 5000,
                    });
                    newBuildTime = buildTimeResponse.data;
                    
                    if (newBuildTime !== oldBuildTime) {
                        buildTimeChanged = true;
                        setDeployStatus({
                            success: true,
                            message: 'Deployment successful',
                            details: {
                                ...data,
                                oldBuildTime: oldBuildTime,
                                newBuildTime: newBuildTime,
                            },
                        });
                        // Refresh releases list and current version
                        fetchReleases();
                        fetchCurrentVersion();
                        break;
                    }
                } catch (e) {
                    console.warn('Error polling build-time:', e.message);
                }

                // Check if polling was cancelled
                if (pollingCancelledRef.current) {
                    break;
                }

                // Update status to show polling
                setDeployStatus({
                    success: false,
                    message: 'Waiting for deployment to complete...',
                    polling: true,
                    details: {
                        ...data,
                        oldBuildTime: oldBuildTime,
                    },
                });

                await new Promise(resolve => setTimeout(resolve, pollInterval));
            }

            // If build-time didn't change within timeout and wasn't cancelled
            if (!buildTimeChanged && !pollingCancelledRef.current) {
                setDeployStatus({
                    success: true,
                    message: 'Deployment script executed, but build-time did not change within timeout period',
                    details: {
                        ...data,
                        oldBuildTime: oldBuildTime,
                        newBuildTime: newBuildTime,
                        warning: 'Build-time did not change - please verify deployment manually',
                    },
                });
                // Refresh anyway
                fetchReleases();
                fetchCurrentVersion();
            }
        } catch (e) {
            if (!pollingCancelledRef.current) {
                setDeployStatus({
                    success: false,
                    message: 'Deployment failed: ' + (e.response?.data?.detail || e.message),
                });
            }
        } finally {
            if (!pollingCancelledRef.current) {
                setDeploying(false);
            }
        }
    }

    function formatDate(date) {
        if (!date) return 'N/A';
        const dayjs = require('dayjs');
        const advancedFormat = require('dayjs/plugin/advancedFormat');
        dayjs.extend(advancedFormat);
        return dayjs(date).format('MMMM Do YYYY, h:mm A');
    }

    function isDeployed(releaseKey) {
        if (!currentVersion || !releaseKey) return false;
        // Compare release key with current build-time
        // Handle both exact matches and cases where the key might be a path or have extensions
        return releaseKey === currentVersion || releaseKey.includes(currentVersion);
    }

    return (
        <Box sx={{ m: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h4">Release Management</Typography>
                <Button 
                    variant="contained" 
                    onClick={() => {
                        fetchReleases();
                        fetchCurrentVersion();
                    }}
                    disabled={loading}
                >
                    {loading ? <CircularProgress size={24} /> : 'Refresh'}
                </Button>
            </Box>

            <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body1">
                    <strong>Current version:</strong> {currentVersion || 'Loading...'}
                </Typography>
            </Alert>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {loading && releases.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell><strong>Version Name</strong></TableCell>
                                <TableCell><strong>Last Modified</strong></TableCell>
                                <TableCell align="right"><strong>Size</strong></TableCell>
                                <TableCell align="right"><strong>Actions</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {releases.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={4} align="center">
                                        <Typography variant="body2" color="text.secondary">
                                            No releases found
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                releases.map((release, index) => (
                                    <TableRow key={index} hover>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                                {release.key}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            {formatDate(release.lastModified)}
                                        </TableCell>
                                        <TableCell align="right">
                                            {(release.size / 1024 / 1024).toFixed(2)} MB
                                        </TableCell>
                                        <TableCell align="right">
                                            {isDeployed(release.key) ? (
                                                <Chip 
                                                    label="Deployed" 
                                                    color="success" 
                                                    size="small"
                                                />
                                            ) : (
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    onClick={() => handleDeployClick(release.key)}
                                                    disabled={deploying}
                                                >
                                                    Deploy
                                                </Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Dialog 
                open={deployDialogOpen} 
                onClose={handleDeployCancel}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Deploy Release</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Are you sure you want to deploy the following release?
                    </Typography>
                    <TextField
                        fullWidth
                        label="Version"
                        value={selectedVersion}
                        disabled
                        sx={{ mb: 2 }}
                    />
                    {deployStatus && (
                        <Alert 
                            severity={deployStatus.success ? 'success' : deployStatus.polling ? 'info' : 'error'} 
                            sx={{ mt: 2 }}
                        >
                            <Typography variant="body2">{deployStatus.message}</Typography>
                            {deployStatus.details && deployStatus.details.oldBuildTime && (
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                    <strong>Previous build time:</strong> {deployStatus.details.oldBuildTime}
                                </Typography>
                            )}
                            {deployStatus.details && deployStatus.details.newBuildTime && (
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                    <strong>New build time:</strong> {deployStatus.details.newBuildTime}
                                </Typography>
                            )}
                            {deployStatus.details && deployStatus.details.warning && (
                                <Typography variant="body2" sx={{ mt: 1, color: 'warning.main' }}>
                                    <strong>Warning:</strong> {deployStatus.details.warning}
                                </Typography>
                            )}
                        </Alert>
                    )}
                    {deploying && (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                {deployStatus?.polling ? 'Waiting for build-time to change...' : 'Deploying release... This may take a few minutes.'}
                            </Typography>
                            <LinearProgress />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeployCancel} disabled={deploying && !deployStatus?.success}>
                        {deployStatus?.success ? 'Close' : 'Cancel'}
                    </Button>
                    {!deployStatus && (
                        <Button 
                            onClick={handleDeployConfirm} 
                            variant="contained"
                            disabled={deploying || !selectedVersion}
                        >
                            {deploying ? <CircularProgress size={24} /> : 'Deploy'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default Releases;

